/**
 * MDF quiz — Minimum Defence Frequency.
 *
 * "Villain bets Xbb into Ybb. What % of your range must defend
 * (call or raise, not fold) to make their bluffs break even?"
 *
 * MDF = pot / (pot + bet). Bigger bets demand fewer defends.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { Stat } from "@/features/quiz/components/Stat";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  describeFraction,
  generateQuestion,
  type MdfQuestion,
} from "@/features/quiz/lib/mdfQuiz";

export function QuizMdf() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<MdfQuestion, number>({
      quizType: "mdf",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.options[q.correctIndex]!,
      getQuestionPayload: (q) => ({
        pot: q.pot,
        bet: q.bet,
        bet_fraction: q.betFraction,
      }),
    });

  const total = question.pot + question.bet;

  return (
    <QuizLayout
      Icon={Brain}
      title="MDF"
      subtitle="Minimum Defence Frequency vs villain's bet"
      description="Tests the minimum % of your range that must defend (call or raise) against villain's bet to prevent their bluffs from auto-profiting. Fold more than MDF and an unbalanced bettor can print money with any two cards. The defender's-side companion to pot odds: knowing it is what makes you unexploitable to overbluffers."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-3 py-4">
        <div className="flex items-baseline gap-8">
          <Stat label="POT" value={`${question.pot}bb`} />
          <Stat label="VILLAIN BETS" value={`${question.bet}bb`} accent />
        </div>
      </div>

      <p className="text-center text-text-muted text-sm">
        Minimum % of your range you must defend?
      </p>

      <div className="grid grid-cols-2 gap-3">
        {question.options.map((pct) => (
          <QuizOption
            key={pct}
            value={pct}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-lg">{pct}%</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}%</strong>
            </>
          }
          rationale={
            <>
              MDF = pot / (pot + bet) = {question.pot} / {total} ≈ {correctAnswer}%. A {describeFraction(question.betFraction)} bet only forces you to defend ~{correctAnswer}% of your range: bigger bets demand fewer defends.
            </>
          }
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}

